import {
    FormControl,
    FormHelperText,
    Icon,
    IconButton,
    InputLabel,
    MenuItem
} from '@material-ui/core';
import { observer } from 'mobx-react';
import React from 'react';

import { Spinner } from '../../../components/spinner';
import { CancelContractManualRequestDto } from '../../../core/api/dtos';
import { WrappedField } from '../../../core/common/forms';
import { injectTSDI } from '../../../core/common/tsdi';
import { I18n } from '../../../core/i18n';
import { StyledSelect } from '../../../core/styles';
import { SharedContractDissolutionStore } from '../../shared-contract-dissolution-store';
import { StyledBlackLabel } from '../../styles';

interface ReasonSelectProps {
    cancellationTypeField: WrappedField<
        CancelContractManualRequestDto['cancellationType']
    >;
    cancellationReasonIdField: WrappedField<
        CancelContractManualRequestDto['cancelationReasonId']
    >;
    studioId?: number;
}

export const ReasonSelect = observer((props: ReasonSelectProps) => {
    const { translate } = injectTSDI(I18n);
    const { getStudio, getStudioInfo } = injectTSDI(
        SharedContractDissolutionStore
    );
    const { studioId, cancellationTypeField, cancellationReasonIdField } =
        props;

    const studio = getStudio(studioId);

    if (!studio) {
        return null;
    }

    const deleteReasonIdButton =
        cancellationReasonIdField.value &&
        cancellationTypeField.value === 'ORDINARY_CANCELLATION' ? (
            <IconButton
                onClick={() => cancellationReasonIdField.reset(undefined)}
            >
                <Icon fontSize="inherit">close</Icon>
            </IconButton>
        ) : undefined;

    return (
        <FormControl
            fullWidth
            variant="outlined"
            required={
                cancellationTypeField.value === 'EXTRAORDINARY_CANCELLATION'
            }
            error={Boolean(cancellationReasonIdField.error)}
            disabled={!studioId}
        >
            <InputLabel
                id="contract-cancellation-reason-label"
                htmlFor="contract-cancellation-reason-input"
            >
                {translate(
                    'public.content.terminate.contract.cancellation.reason.label'
                )}
            </InputLabel>
            <StyledSelect
                labelId="contract-cancellation-reason-input"
                value={cancellationReasonIdField.value || ''}
                onChange={(event) =>
                    cancellationReasonIdField.reset(
                        event.target.value
                            ? parseInt(event.target.value as string, 10)
                            : undefined
                    )
                }
                label={
                    <StyledBlackLabel>
                        {translate(
                            'public.content.terminate.contract.cancellation.reason.label'
                        )}
                    </StyledBlackLabel>
                }
                data-role="contract-termination-reasons"
                endAdornment={deleteReasonIdButton}
                inputProps={{
                    id: 'contract-cancellation-reason-input'
                }}
            >
                {getStudioInfo.match({
                    fetched: (data) => {
                        return (data?.cancellationReasons || [])?.map(
                            (cancelationReason) => (
                                <MenuItem
                                    key={cancelationReason.databaseId}
                                    value={cancelationReason.databaseId}
                                    data-role="contract-termination-reason"
                                >
                                    {cancelationReason.name}
                                </MenuItem>
                            )
                        );
                    },
                    loading: () => (
                        <MenuItem disabled>
                            <Spinner />
                        </MenuItem>
                    )
                })}
            </StyledSelect>
            {cancellationReasonIdField.error && (
                <FormHelperText>
                    {translate(cancellationReasonIdField.error)}
                </FormHelperText>
            )}
        </FormControl>
    );
});
